import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { PlatformPage } from "@/components/PlatformPage";
import { getSettings, isPlatformSettings } from "../api";
import { AdminAccessPanel } from "../components/AdminAccessPanel";
import { CommissionSettingsPanel } from "../components/CommissionSettingsPanel";
import { CampaignFeeSettingsPanel } from "../components/CampaignFeeSettingsPanel";
import { FeatureFlagsPanel } from "../components/FeatureFlagsPanel";
import { MaintenancePanel } from "../components/MaintenancePanel";
import { InquiryMessageSettingsPanel } from "../components/InquiryMessageSettingsPanel";
import { PLATFORM_SETTINGS_QUERY_KEY } from "../settingsAuthority";
import { useSettingsAuthority } from "../settingsAuthorityStore";

/** Platform settings grouped by saved facts and their allowed actions. */
export function PlatformSettingsPage() {
  const queryClient = useQueryClient();
  const authority = useSettingsAuthority(queryClient);
  const settingsQuery = useQuery({
    queryKey: PLATFORM_SETTINGS_QUERY_KEY,
    queryFn: getSettings,
  });
  const settings = isPlatformSettings(settingsQuery.data)
    ? settingsQuery.data
    : null;
  const initialReadFailed =
    !settings && (settingsQuery.isError || settingsQuery.isSuccess);
  const staleReadFailed = Boolean(settings && settingsQuery.isError);

  return (
    <PlatformPage
      title="Platform Settings"
      isLoading={!settings && settingsQuery.isLoading}
      isError={initialReadFailed}
    >
      {settings ? (
        <Stack spacing={3}>
          {staleReadFailed && authority.recovery === null ? (
            <Alert
              severity="warning"
              role="alert"
              action={
                <Button
                  color="inherit"
                  size="small"
                  disabled={settingsQuery.isFetching}
                  onClick={() => void settingsQuery.refetch()}
                >
                  Retry
                </Button>
              }
            >
              Settings could not be refreshed. Showing the last confirmed
              values.
            </Alert>
          ) : null}
          <CommissionSettingsPanel settings={settings} />
          <CampaignFeeSettingsPanel settings={settings} />
          <InquiryMessageSettingsPanel settings={settings} />
          <AdminAccessPanel />
          <FeatureFlagsPanel settings={settings} />
          <MaintenancePanel settings={settings} />
        </Stack>
      ) : null}
    </PlatformPage>
  );
}
